import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  FileText,
  BarChart3,
  Users,
  ClipboardList,
  Building2,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth-store'
import { useUIStore } from '@/store/ui-store'
import { useNotificationStore } from '@/store/notification-store'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'

// Inline SVG icons — avoids lucide-react named export issues
function HelpCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  )
}

/* AI Opportunities icon — layered diamond/stack shape */
function OpportunitiesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  )
}

/* Invoice icon — receipt/document */
function InvoiceIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 3 2V2l-3 2-3-2-3 2-3-2-3 2-3-2z" />
      <path d="M8 10h8" />
      <path d="M8 14h4" />
    </svg>
  )
}

/* Pricing parent icon — dollar sign */
function PricingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  )
}

/* Chevron for accordion */
function ChevronIcon({ className, open }: { className?: string; open: boolean }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms ease' }}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}

interface NavItem {
  label: string
  href: string
  icon: React.ElementType
  roles?: string[]
}

interface PricingSubItem {
  label: string
  href: string
  roles?: string[]
}

const mainNav: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Opportunities', href: '/opportunities', icon: OpportunitiesIcon, roles: ['SUPERADMIN', 'ADMIN', 'MANAGER', 'ESTIMATOR'] },
  { label: 'Estimates', href: '/estimates', icon: ClipboardList },
  { label: 'Proposals', href: '/proposals', icon: FileText },
  { label: 'Invoices', href: '/invoices', icon: InvoiceIcon },
  { label: 'Reports', href: '/reports', icon: BarChart3, roles: ['SUPERADMIN', 'ADMIN', 'MANAGER'] },
  { label: 'Customers', href: '/customers', icon: Users },
]

const pricingSubItems: PricingSubItem[] = [
  { label: 'Equipment', href: '/pricing/equipment', roles: ['SUPERADMIN', 'ADMIN'] },
  { label: 'Laborers', href: '/pricing/laborers', roles: ['SUPERADMIN', 'ADMIN'] },
  { label: 'Materials', href: '/pricing/materials', roles: ['SUPERADMIN', 'ADMIN'] },
  { label: 'Miscellaneous', href: '/pricing/miscellaneous', roles: ['SUPERADMIN', 'ADMIN'] },
  { label: 'Striping', href: '/pricing/striping', roles: ['SUPERADMIN', 'ADMIN'] },
  { label: 'Inclusions/Exclusions', href: '/pricing/exclusions', roles: ['SUPERADMIN', 'ADMIN'] },
  { label: 'Haul & Dump', href: '/pricing/haul-dump', roles: ['SUPERADMIN', 'ADMIN'] },
  { label: 'Fuel Charge', href: '/pricing/defaults', roles: ['SUPERADMIN', 'ADMIN'] },
  // { label: 'Import / Export', href: '/pricing/import', roles: ['SUPERADMIN', 'ADMIN'] },
  // { label: 'Crews', href: '/pricing/crews', roles: ['SUPERADMIN', 'ADMIN'] },
]

const settingsNav: NavItem[] = [
  { label: 'Company Settings', href: '/settings/company', icon: Building2, roles: ['SUPERADMIN', 'ADMIN'] },
  { label: 'User Management', href: '/settings/users', icon: Users, roles: ['SUPERADMIN', 'ADMIN'] },
]

const supportNav: NavItem[] = [
  { label: 'Help & FAQ', href: '/help', icon: HelpCircleIcon },
]

function NavSection({ title, items }: { title?: string; items: NavItem[] }) {
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const { sidebarCollapsed, setSidebarOpen } = useUIStore()
  const notifications = useNotificationStore((s) => s.notifications)
  const newAssignmentCount = notifications.filter(
    (n) => !n.isRead && n.type === 'opportunity_assigned'
  ).length

  const userRole = user?.role || ''
  const filteredItems = items.filter(
    (item) => !item.roles || item.roles.includes(userRole)
  )

  if (filteredItems.length === 0) return null

  return (
    <div className="px-3 py-2">
      {title && !sidebarCollapsed && (
        <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/50">
          {title}
        </p>
      )}
      <nav className="space-y-1">
        {filteredItems.map((item) => {
          const isSettingsPreview = location.pathname.startsWith('/proposals/preview') && location.search.includes('mode=settings-preview')
          const isProposalPreview = location.pathname === '/proposals/preview' && !location.search.includes('mode=settings-preview')
          const isFromProposals = location.search.includes('from=/proposals')
          const isActive = isSettingsPreview
            ? item.href === '/settings/company'
            : isProposalPreview && isFromProposals
              ? item.href === '/proposals'
              : isProposalPreview
                ? item.href === '/estimates'
                : (location.pathname === item.href || location.pathname.startsWith(item.href + '/'))

          const showBadge = item.href === '/opportunities' && userRole === 'ESTIMATOR' && newAssignmentCount > 0

          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={cn(
                'relative flex items-center gap-3 rounded-lg px-3 py-2 text-[15px] font-medium transition-colors',
                isActive
                  ? 'bg-[#BEC4F2]/[0.08] text-white'
                  : 'text-[#BEC4F2] hover:bg-[#BEC4F2]/[0.08] hover:text-white'
              )}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!sidebarCollapsed && (
                <span className="flex items-start gap-1">
                  <span>{item.label}</span>
                  {showBadge && (
                    <span className="sb-opp-badge">
                      {newAssignmentCount > 99 ? '99+' : newAssignmentCount}
                    </span>
                  )}
                </span>
              )}
              {sidebarCollapsed && showBadge && (
                <span className="sb-opp-badge sb-opp-badge-collapsed" />
              )}
            </Link>
          )
        })}
      </nav>
    </div>
  )
}

function PricingAccordion() {
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const { sidebarCollapsed, setSidebarOpen } = useUIStore()
  const userRole = user?.role || ''

  const filteredSubs = pricingSubItems.filter(
    (item) => !item.roles || item.roles.includes(userRole)
  )

  // Auto-open if current path is a pricing sub-route
  const isPricingRoute = location.pathname.startsWith('/pricing')
  const [open, setOpen] = useState(isPricingRoute)

  // Keep open when navigating into pricing
  useEffect(() => {
    if (isPricingRoute) setOpen(true)
  }, [isPricingRoute])

  if (filteredSubs.length === 0) return null

  return (
    <div className="px-3 py-2">
      {/* Pricing parent row */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'sb-accordion-trigger flex w-full items-center gap-3 rounded-lg px-3 py-2 text-[15px] font-medium transition-colors',
          isPricingRoute
            ? 'text-white'
            : 'text-[#BEC4F2] hover:bg-[#BEC4F2]/[0.08] hover:text-white'
        )}
      >
        <PricingIcon className="h-5 w-5 shrink-0" />
        {!sidebarCollapsed && (
          <>
            <span className="flex-1 text-left">Pricing</span>
            <ChevronIcon className="shrink-0 opacity-50" open={open} />
          </>
        )}
      </button>

      {/* Sub-items — animated */}
      {!sidebarCollapsed && (
        <div
          className="sb-accordion-panel"
          style={{
            display: 'grid',
            gridTemplateRows: open ? '1fr' : '0fr',
            transition: 'grid-template-rows 200ms ease',
          }}
        >
          <div style={{ overflow: 'hidden' }}>
            <nav className="sb-accordion-items mt-1 space-y-0.5" style={{ paddingLeft: '32px' }}>
              {filteredSubs.map((item) => {
                const isActive =
                  location.pathname === item.href ||
                  location.pathname.startsWith(item.href + '/')
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      'sb-accordion-link block rounded-md px-3 py-1.5 text-[14px] transition-colors',
                      isActive
                        ? 'text-white font-medium'
                        : 'text-[#BEC4F2]/70 hover:text-white hover:bg-[#BEC4F2]/[0.06]'
                    )}
                  >
                    {item.label}
                  </Link>
                )
              })}
            </nav>
          </div>
        </div>
      )}
    </div>
  )
}

function hasVisibleItems(items: NavItem[], role: string) {
  return items.some((item) => !item.roles || item.roles.includes(role))
}

function hasPricingAccess(role: string) {
  return pricingSubItems.some((item) => !item.roles || item.roles.includes(role))
}

const COLLAPSE_BREAKPOINT = 1280

export function Sidebar() {
  const { sidebarOpen, setSidebarOpen, sidebarCollapsed, setSidebarCollapsed } = useUIStore()
  const user = useAuthStore((s) => s.user)
  const userRole = user?.role || ''

  useEffect(() => {
    const handleResize = () => {
      setSidebarCollapsed(window.innerWidth < COLLAPSE_BREAKPOINT)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [setSidebarCollapsed])

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col transition-all duration-300',
          sidebarCollapsed ? 'w-16' : 'w-64',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
        style={{ background: '#0C1430', borderRight: '1px solid #1A2342' }}
      >
        {/* Logo / Brand */}
        <div className="flex items-center justify-between px-4" style={{ borderBottom: '1px solid #1A2342', height: '69px' }}>
          {!sidebarCollapsed && (
            <Link to="/dashboard" className="flex items-center">
              <img src="/images/paver-logo.png" alt="Paver" style={{ width: '130px', height: 'auto' }} />
            </Link>
          )}
          {sidebarCollapsed && (
            <Link to="/dashboard" className="mx-auto flex items-center justify-center">
              <img src="/images/paver-mark.png" alt="Paver" style={{ width: '30px', height: 'auto' }} />
            </Link>
          )}
          {/* Close button for mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-sidebar-foreground"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-4">
          <NavSection items={mainNav.filter(item => {
            // Gate the Invoices nav item based on tenant settings
            if (item.href === '/invoices') {
              // Default to true if the field hasn't been set yet (on by default)
              const enabled = user?.invoicingEnabled !== false
              if (!enabled) return false
              // If roles are defined, check access. Otherwise default to admin+manager
              const allowedRoles = (user?.invoicingRoles && user.invoicingRoles.length > 0)
                ? user.invoicingRoles.map((r: string) => r.toUpperCase())
                : ['ADMIN', 'MANAGER']
              return allowedRoles.includes(userRole) || userRole === 'SUPERADMIN'
            }
            return true
          })} />
          {hasPricingAccess(userRole) && <Separator className="my-2 bg-sidebar-border" />}
          <PricingAccordion />
          {hasVisibleItems(settingsNav, userRole) && <Separator className="my-2 bg-sidebar-border" />}
          <NavSection title="Admin" items={settingsNav} />
          <Separator className="my-2 bg-sidebar-border" />
          <NavSection title="Support" items={supportNav} />
        </div>
      </aside>
    </>
  )
}
